// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef, useState } from 'react';
import { useRootNavigationState, useRouter } from 'expo-router';

import type { UserSession } from '@/app/user-context';
import { exchangeCode } from '@/auth/oidc';
import { consumePostLoginPath } from '@/auth/postLoginPath';

export const useAuthCallback = (params: {
  code?: string;
  state?: string;
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  onError?: (message: string) => void;
}) => {
  const { code, state, user, setUser, onError } = params;
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const [loading, setLoading] = useState(false);
  const exchangeRef = useRef<{ code: string | null; inFlight: boolean }>({ code: null, inFlight: false });

  useEffect(() => {
    const run = async () => {
      if (user) return;
      if (!code) {
        onError?.('Saknar code från Keycloak.');
        return;
      }
      if (exchangeRef.current.inFlight || exchangeRef.current.code === code) {
        return;
      }
      exchangeRef.current = { code, inFlight: true };
      try {
        setLoading(true);
        const result = await exchangeCode(code, state);
        setUser({
          id: result.id,
          forename: result.name,
          token: result.accessToken,
          idToken: result.idToken ?? null,
          refreshToken: result.refreshToken ?? null,
        });
        const storedReturnTo = await consumePostLoginPath();
        if (isNavigationReady) {
          router.replace(storedReturnTo || '/welcome');
        }
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Token-utbyte misslyckades');
        exchangeRef.current = { code, inFlight: false };
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [code, isNavigationReady, state, onError, router, setUser, user]);

  return { loading };
};
